import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Package, Truck, MapPin, ArrowRight } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";

const OrderSuccessPage = () => {
  const { orderId } = useParams();
  const { API, settings } = useApp();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await axios.get(`${API}/orders/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        console.error("Failed to fetch order:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [orderId, API]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Order Not Found</h2>
          <Link to="/">
            <Button>Go to Home</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12" data-testid="order-success-page">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-2xl">
        {/* Success Header */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2" data-testid="success-title">
            Order Confirmed!
          </h1>
          <p className="text-muted-foreground">
            Thank you for your order. We'll send you a confirmation email shortly.
          </p>
        </div>

        {/* Order Info Card */}
        <div className="bg-white rounded-xl border p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm text-muted-foreground">Order ID</p>
              <p className="font-mono font-medium" data-testid="order-id">{order.id}</p>
            </div>
            <div className={`status-badge status-${order.order_status}`} data-testid="order-status">
              {order.order_status}
            </div>
          </div>

          {/* Order Items */}
          <div className="border-t pt-4">
            <h3 className="font-semibold mb-4 flex items-center gap-2">
              <Package className="w-5 h-5" />
              Order Items
            </h3>
            <div className="space-y-3">
              {order.items.map((item, index) => (
                <div key={index} className="flex gap-3">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                    <img
                      src={item.image_url || "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=100"}
                      alt={item.product_name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">{item.product_name}</p>
                    <p className="text-sm text-muted-foreground">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-medium">₹{(item.price * item.quantity).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Price Summary */}
          <div className="border-t mt-4 pt-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Subtotal</span>
              <span>₹{order.subtotal.toLocaleString()}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Delivery</span>
              <span>{order.delivery_charge === 0 ? "FREE" : `₹${order.delivery_charge}`}</span>
            </div>
            <div className="flex justify-between font-bold text-lg pt-2 border-t">
              <span>Total</span>
              <span className="text-primary" data-testid="order-total">₹{order.total.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* Delivery Address */}
        <div className="bg-white rounded-xl border p-6 mb-6">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            Delivery Address
          </h3>
          <div className="text-muted-foreground">
            <p className="font-medium text-foreground">{order.delivery_address.full_name}</p>
            <p>{order.delivery_address.address_line1}</p>
            {order.delivery_address.address_line2 && <p>{order.delivery_address.address_line2}</p>}
            <p>{order.delivery_address.city}, {order.delivery_address.state} - {order.delivery_address.pincode}</p>
            <p>Phone: {order.delivery_address.phone}</p>
          </div>
        </div>

        {/* Estimated Delivery */}
        <div className="bg-primary/5 rounded-xl border border-primary/20 p-6 mb-6">
          <div className="flex items-center gap-3">
            <Truck className="w-6 h-6 text-primary" />
            <div>
              <p className="font-semibold">Estimated Delivery</p>
              <p className="text-sm text-muted-foreground">
                Your order will be delivered within 3-5 business days
              </p>
            </div>
          </div>
        </div>

        {/* WhatsApp Share + Track */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <button
            onClick={() => {
              const items = order.items.map(i => `${i.product_name} x${i.quantity}`).join(", ");
              const msg = `Hi! I just placed an order on MiniBegumBazaar.\nOrder #${order.id.slice(0,8)}\nItems: ${items}\nTotal: ₹${order.total}\nPlease confirm my order.`;
              window.open(`https://wa.me/${settings?.whatsapp_number || "919441114344"}?text=${encodeURIComponent(msg)}`, "_blank");
            }}
            className="flex-1 flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white font-medium py-3 px-6 rounded-lg transition-colors"
            data-testid="whatsapp-share-order"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Confirm on WhatsApp
          </button>
          <Link to={`/track-order/${order.id}`} className="flex-1">
            <Button variant="outline" className="w-full" size="lg" data-testid="track-order-button">
              Track Your Order
            </Button>
          </Link>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/categories" className="flex-1">
            <Button className="w-full btn-primary" size="lg" data-testid="continue-shopping">
              Continue Shopping
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
          <Link to="/" className="flex-1">
            <Button variant="outline" className="w-full" size="lg" data-testid="go-home">
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
